// 萬聖節南瓜飄浮層（自適應：先量度內容欄寬度，再把南瓜放在真正的空白位）
// 用法：<Pumpkins /> 放在任何 root；用 portal + 動態量度，唔會被 re-render 移除，
// 亦唔會蓋住置中內容（登入卡、表單、圖表）。

import React from "react";
import ReactDOM from "react-dom";

const COLORS: [string, string, string][] = [
  ["#F59E0B", "#FBBF24", "#B45309"],
  ["#FBBF24", "#FCD34D", "#92400E"],
  ["#7C3AED", "#A78BFA", "#4C1D95"],
];

type Placed = { left: number; top: number; size: number; blur: number; dur: number; delay: number; kind: 0 | 1 };

/** 量度真正可用的空白位：左右兩條 gutter；太窄就改用上／下兩條橫帶 */
function layout(): Placed[] {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // 內容欄大約 1040px（登入卡、表格、dashboard 主欄都喺呢個範圍內）
  const contentW = Math.min(1040, Math.max(360, vw * 0.94));
  const gutter = Math.max(0, (vw - contentW) / 2);

  // 有側邊欄的 app（agent / dashboard）要避開左邊欄
  let leftReserve = 8;
  try {
    const side = document.querySelector('aside, nav, [role="complementary"]') as HTMLElement | null;
    if (side) {
      const r = side.getBoundingClientRect();
      if (r.left < 24 && r.width > 120) leftReserve = r.right + 10;
    }
  } catch (_) {}
  const leftSpan = Math.max(0, Math.min(gutter - 12, 150));
  const out: Placed[] = [];

  const push = (left: number, top: number, size: number) => {
    out.push({
      left,
      top,
      size,
      blur: size > 44 ? 1.5 : 0,
      dur: 9 + (out.length % 5) * 1.3,
      delay: out.length * 0.55,
      kind: (out.length % 2) as 0 | 1,
    });
  };

  if (gutter >= 64) {
    const n = 6;
    for (let i = 0; i < n; i++) {
      const y = 8 + (i * 84) / n + Math.random() * 6;
      const size = 24 + Math.random() * 14;
      // 有側邊欄就完全唔放左邊，避免壓住 menu 同按鈕
      if (leftReserve <= 20) push(leftReserve + Math.random() * leftSpan, y, size);
      push(vw - 10 - size - Math.random() * Math.max(4, gutter - 40), y + 4, size * 0.9);
    }
  } else {
    // 窄螢幕：只用頂部與底部兩條橫帶（內容通常唔會去到最邊）
    for (let i = 0; i < 7; i++) {
      push((i * vw) / 7 + Math.random() * (vw / 14), 4 + Math.random() * 40, 22 + Math.random() * 12);
      push((i * vw) / 7 + Math.random() * (vw / 14), vh - 46 - Math.random() * 30, 20 + Math.random() * 12);
    }
  }
  return out;
}

export default function Pumpkins({ opacity = 1 }: { opacity?: number }) {
  const [items, setItems] = React.useState<Placed[]>([]);

  React.useEffect(() => {
    const update = () => setItems(layout());
    update();
    let t: number | undefined;
    const onResize = () => {
      window.clearTimeout(t);
      t = window.setTimeout(update, 200);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.clearTimeout(t);
    };
  }, []);

  const layer = (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
        zIndex: 2147483000,
        opacity,
      }}
    >
      <style>{`
        @keyframes pk-float { 0%,100%{transform:translate3d(0,0,0) rotate(-6deg)} 50%{transform:translate3d(5px,-18px,0) rotate(6deg)} }
        @keyframes pk-drift { 0%,100%{transform:translate3d(0,0,0) rotate(4deg)} 50%{transform:translate3d(-8px,-12px,0) rotate(-7deg)} }
        @media (prefers-reduced-motion: reduce) { .pk { animation: none !important } }
      `}</style>
      {items.map((p, i) => {
        const c = COLORS[i % COLORS.length];
        return (
          <span
            key={i}
            className="pk"
            style={{
              position: "absolute",
              left: p.left,
              top: `${p.top}%`,
              opacity: 0.26 + (i % 3) * 0.08,
              filter: p.blur ? `blur(${p.blur}px)` : undefined,
              animation: `${p.kind ? "pk-float" : "pk-drift"} ${p.dur}s ease-in-out ${p.delay}s infinite`,
              willChange: "transform",
            }}
          >
            <svg width={p.size} height={p.size} viewBox="0 0 64 64">
              <ellipse cx="32" cy="38" rx="20" ry="17" fill={c[0]} />
              <ellipse cx="22" cy="38" rx="10" ry="16" fill={c[1]} />
              <ellipse cx="42" cy="38" rx="10" ry="16" fill={c[1]} />
              <path d="M32 23c0-7 3-10 8-10" stroke="#15803D" strokeWidth="4.5" fill="none" strokeLinecap="round" />
              <circle cx="26" cy="37" r="2.4" fill={c[2]} />
              <circle cx="38" cy="37" r="2.4" fill={c[2]} />
              <path d="M25 45q7 6 14 0" stroke={c[2]} strokeWidth="2.6" fill="none" strokeLinecap="round" />
            </svg>
          </span>
        );
      })}
    </div>
  );

  if (typeof document === "undefined") return null;
  return ReactDOM.createPortal(layer, document.body);
}
